// ----------------------------------------------------------------------

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';

/**
 * The tabs of the professional's live visit page, on either route variant.
 *
 * An authed visit hangs its tabs off the patient: /visit/:id/patient/:pid/notes.
 * A guest visit has no patient to hang them off, so they sit directly under the
 * visit: /visit/:id/notes. Callers pass whatever patientId they have — empty on
 * a guest visit — and get the right shape either way.
 */
export type VisitTab =
  | 'notes'
  | 'details'
  | 'history'
  | 'medications'
  | 'reminders'
  | 'treatments'
  | 'files';

export type VoiceReceptionistDashboardTab = 'settings' | 'history';

export function visitTabPath(
  visitId: string,
  patientId: string,
  tab?: VisitTab,
): string {
  const base = patientId
    ? `/visit/${visitId}/patient/${patientId}`
    : `/visit/${visitId}`;
  return path(ROOTS_DASHBOARD, tab ? `${base}/${tab}` : base);
}

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  phoneLogin: path(ROOTS_AUTH, '/phone-login'),
  register: path(ROOTS_AUTH, '/register'),
  professionalRegister: path(ROOTS_AUTH, '/professional-register'),
  professionalLogin: path(ROOTS_AUTH, '/professional-login'),
  organizationSetup: path(ROOTS_AUTH, '/organization-setup'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify'),
  confirmation: path(ROOTS_AUTH, '/confirmation'),
  professionalConfirmation: path(
    ROOTS_AUTH,
    '/confirmation?account=professional',
  ),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  requestSent: path(ROOTS_AUTH, '/request-sent'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
  acceptTerms: path(ROOTS_AUTH, '/accept-terms'),
  accountClosed: path(ROOTS_AUTH, '/account-closed'),
  pendingDeletion: path(ROOTS_AUTH, '/pending-deletion'),
  termsConditionsUser: path(ROOTS_AUTH, '/terms-conditions/user'),
  termsConditionsProfessional: path(
    ROOTS_AUTH,
    '/terms-conditions/professional',
  ),
  privacyPolicy: path(ROOTS_AUTH, '/privacy-policy/user'),
  privacyPolicyProfessional: path(ROOTS_AUTH, '/privacy-policy/professional'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
};

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  calendar: path(ROOTS_DASHBOARD, '/calendar'),
  professionalSchedule: path(ROOTS_DASHBOARD, '/professional-schedule'),
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  warehouse: {
    root: path(ROOTS_DASHBOARD, '/warehouse'),
    products: path(ROOTS_DASHBOARD, '/warehouse/products'),
    documents: path(ROOTS_DASHBOARD, '/warehouse/documents'),
    demand: path(ROOTS_DASHBOARD, '/warehouse/demand'),
    sales: path(ROOTS_DASHBOARD, '/warehouse/sales'),
    suppliers: path(ROOTS_DASHBOARD, '/warehouse/suppliers'),
  },
  webinars: {
    root: path(ROOTS_DASHBOARD, '/webinars'),
    event: (webinarId: string) =>
      path(ROOTS_DASHBOARD, `/webinars/${webinarId}`),
  },
  general: {
    updateAccount: path(ROOTS_DASHBOARD, '/update-account'),
    updateAccountInvitation: path(
      ROOTS_DASHBOARD,
      '/update-account/invitation',
    ),
    account: {
      root: path(ROOTS_DASHBOARD, '/account'),
      general: path(ROOTS_DASHBOARD, '/account/general'),
      business: path(ROOTS_DASHBOARD, '/account/business'),
      password: path(ROOTS_DASHBOARD, '/account/password'),
      factPreferences: path(ROOTS_DASHBOARD, '/account/fact-preferences'),
    },
    notifications: {
      root: path(ROOTS_DASHBOARD, '/notifications'),
      invites: path(ROOTS_DASHBOARD, '/notifications/invites'),
    },
    petFacts: path(ROOTS_DASHBOARD, '/pet-facts'),
    book: {
      root: path(ROOTS_DASHBOARD, '/search'),
      results: path(ROOTS_DASHBOARD, '/search/results'),
      organization: (organizationIdOrSlug: string) =>
        path(ROOTS_DASHBOARD, `/search/results/${organizationIdOrSlug}`),
      booking: (organizationIdOrSlug: string) =>
        path(
          ROOTS_DASHBOARD,
          `/search/results/${organizationIdOrSlug}/booking`,
        ),
    },
    upcomingVisits: path(ROOTS_DASHBOARD, '/upcoming-visits'),
    history: path(ROOTS_DASHBOARD, '/history'),
    insurance: path(ROOTS_DASHBOARD, '/insurance'),
    couponCodes: path(ROOTS_DASHBOARD, '/coupon-codes'),
    profiles: {
      root: path(ROOTS_DASHBOARD, '/profiles'),
      profile: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}`),
      visit: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/visit`),
      notes: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/notes`),
      assistant: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/assistant`),
      visitsHistory: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/visits-history`),
      files: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/files`),
      suggestions: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/suggestions`),
      products: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/products`),
      medications: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/medications`),
      reminders: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/reminders`),
      treatments: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/profiles/${profileId}/treatments`),
    },
    visit: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId),
    // Each tab, on either variant of the visit route. A guest visit has no
    // patient segment, so its tabs live directly under /visit/:visitId.
    visitNotes: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'notes'),
    // The "Wizyta" tab's own path. It used to be the index route, until notes
    // became the tab a professional lands on.
    visitDetails: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'details'),
    visitHistory: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'history'),
    visitMedications: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'medications'),
    visitReminders: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'reminders'),
    visitTreatments: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'treatments'),
    visitFiles: (visitId: string, patientId: string) =>
      visitTabPath(visitId, patientId, 'files'),
    liveVisit: (visitId: string) =>
      path(ROOTS_DASHBOARD, `/live-visit/${visitId}`),
    waitingRoom: path(ROOTS_DASHBOARD, '/waiting-room'),
    visitsHistory: path(ROOTS_DASHBOARD, '/visits-history'),
    treatmentsOverview: path(ROOTS_DASHBOARD, '/treatments'),
    statistics: path(ROOTS_DASHBOARD, '/statistics'),
    calendar: path(ROOTS_DASHBOARD, '/calendar'),
    calendarVisit: (visitId: string): string =>
      path(ROOTS_DASHBOARD, `/calendar?visitId=${encodeURIComponent(visitId)}`),
    voiceReceptionist: {
      root: path(ROOTS_DASHBOARD, '/voice-reception'),
      tab: (tab: VoiceReceptionistDashboardTab): string =>
        `${path(ROOTS_DASHBOARD, '/voice-reception')}?tab=${tab}`,
      call: (callRef: string): string =>
        path(ROOTS_DASHBOARD, `/voice-reception/calls/${callRef}`),
    },
    organizationVerification: path(
      ROOTS_DASHBOARD,
      '/organization-verification',
    ),
    templates: path(ROOTS_DASHBOARD, '/templates'),
    forms: path(ROOTS_DASHBOARD, '/forms'),
    formNew: path(ROOTS_DASHBOARD, '/forms/new'),
    formEdit: (formId: string) => path(ROOTS_DASHBOARD, `/forms/${formId}`),
    formFill: (visitId: string, formId: string) =>
      path(ROOTS_DASHBOARD, `/visits/${visitId}/forms/${formId}`),
    products: {
      root: path(ROOTS_DASHBOARD, '/products'),
      product: (productId: string) =>
        path(ROOTS_DASHBOARD, `/products/${productId}`),
    },
    booking: {
      firstSchedule: path(ROOTS_DASHBOARD, `/booking/first-schedule`),
      schedule: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/booking/schedule/${profileId}`),
      reschedule: (profileId: string, visitId: string) =>
        path(ROOTS_DASHBOARD, `/booking/schedule/${profileId}/${visitId}`),
      completed: (visitId: string) =>
        path(ROOTS_DASHBOARD, `/booking/completed/${visitId}`),
      professionalBooking: (professionalId?: string) =>
        path(ROOTS_DASHBOARD, `/booking/professional/${professionalId}`),
      staffBooking: path(ROOTS_DASHBOARD, `/booking/staff`),
    },
    petsy: {
      schedule: (profileId: string) =>
        path(ROOTS_DASHBOARD, `/booking/petsy-schedule/${profileId}`),
      reschedule: (profileId: string, visitId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/booking/petsy-schedule/${profileId}/${visitId}`,
        ),
      completed: (visitId: string, referer?: string | null) =>
        path(
          ROOTS_DASHBOARD,
          referer
            ? `/booking/petsy-completed/${visitId}?referer=${referer}`
            : `/booking/petsy-completed/${visitId}`,
        ),
    },
    // ADMIN ROUTES
    organizations: {
      root: path(ROOTS_DASHBOARD, '/organizations'),
      organization: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organizations/${organizationId}`),
      schedule: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organizations/${organizationId}/schedule`),
      visitSettings: (organizationId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/visit-settings`,
        ),
      details: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organizations/${organizationId}/details`),
      voiceReceptionist: (organizationId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/voice-receptionist`,
        ),
      professionalDetails: (organizationId: string, professionalId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/details/professional/${professionalId}`,
        ),
      professionalSchedule: (organizationId: string, professionalId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/details/professional/${professionalId}/schedule`,
        ),
      professionalCalendar: (organizationId: string, professionalId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/details/professional/${professionalId}/calendar`,
        ),
      professionalWaitingRoom: (
        organizationId: string,
        professionalId: string,
      ) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/details/professional/${professionalId}/waiting-room`,
        ),
      professionalHistory: (organizationId: string, professionalId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/details/professional/${professionalId}/history`,
        ),
      professionalVisitSettings: (
        organizationId: string,
        professionalId: string,
      ) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/details/professional/${professionalId}/visit-settings`,
        ),
      users: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organizations/${organizationId}/users`),
      invites: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organizations/${organizationId}/invites`),
      campaigns: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organizations/${organizationId}/campaigns`),
      campaignCreate: (organizationId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/campaigns/create`,
        ),
      campaign: (organizationId: string, campaignId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/campaigns/${campaignId}`,
        ),
      campaignCompleted: (organizationId: string, campaignId: string) =>
        path(
          ROOTS_DASHBOARD,
          `/organizations/${organizationId}/campaigns/${campaignId}/completed`,
        ),
    },
    // ORG ADMIN ROUTES
    organization: {
      root: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organization/${organizationId}`),
      schedule: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organization/${organizationId}/schedule`),
      visitSettings: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organization/${organizationId}/visit-settings`),
      details: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organization/${organizationId}/details`),
      invites: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organization/${organizationId}/invites`),
      treatments: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/organization/${organizationId}/treatments`),
    },
    // ORG ADMIN ROUTES
    professionals: {
      root: path(ROOTS_DASHBOARD, `/professionals`),
      professional: (professionalId: string) =>
        path(ROOTS_DASHBOARD, `/professionals/${professionalId}`),
      waitingRoom: (professionalId: string) =>
        path(ROOTS_DASHBOARD, `/professionals/${professionalId}/waiting-room`),
      calendar: (professionalId: string) =>
        path(ROOTS_DASHBOARD, `/professionals/${professionalId}/calendar`),
      history: (professionalId: string) =>
        path(ROOTS_DASHBOARD, `/professionals/${professionalId}/history`),
      schedule: (professionalId: string) =>
        path(ROOTS_DASHBOARD, `/professionals/${professionalId}/schedule`),
    },
    patients: {
      root: path(ROOTS_DASHBOARD, `/patients`),
      patient: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}`),
      visit: (patientId: string, visitId?: string) =>
        path(
          ROOTS_DASHBOARD,
          `/patients/${patientId}/visit${visitId ? `/${visitId}` : ''}`,
        ),
      notes: (patientId: string, visitId?: string) =>
        path(
          ROOTS_DASHBOARD,
          `/patients/${patientId}/notes${visitId ? `/${visitId}` : ''}`,
        ),
      assistant: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}/assistant`),
      history: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}/history`),
      files: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}/files`),
      medications: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}/medications`),
      reminders: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}/reminders`),
      treatments: (patientId: string) =>
        path(ROOTS_DASHBOARD, `/patients/${patientId}/treatments`),
    },
    // Authenticated Search Routes
    search: {
      root: path(ROOTS_DASHBOARD, '/search/results'),
      organization: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/search/results/${organizationId}`),
      booking: (organizationId: string) =>
        path(ROOTS_DASHBOARD, `/search/results/${organizationId}/booking`),
    },
  },

  chat: {
    root: path(ROOTS_DASHBOARD, '/chat'),
    new: path(ROOTS_DASHBOARD, '/chat/new'),
    view: (name: string) => path(ROOTS_DASHBOARD, `/chat/${name}`),
  },
};

export const ROOTS_SEARCH = {
  root: '/search',
};

export const PATH_SEARCH = {
  root: path(ROOTS_SEARCH.root, '/results'),
  organization: (organizationId: string) =>
    path(ROOTS_SEARCH.root, `/results/${organizationId}`),
  booking: (organizationId: string) =>
    path(ROOTS_SEARCH.root, `/results/${organizationId}/booking`),
  bookingCompleted: (organizationId: string, visitId: string) =>
    path(
      ROOTS_SEARCH.root,
      `/results/${organizationId}/booking/completed/${visitId}`,
    ),
};

export const PATH_CLINIC = {
  root: '/placowka',
  details: (slugOrId: string) => `/placowka/${slugOrId}`,
};

export const PATH_GUEST = {
  visit: (visitId: string) => `/visit/guest/${visitId}`,
  manage: (handle: string) => `/g/${handle}`,
};

/** Presenters may have only a basic account, so this lives outside the
 *  dashboard and its clinic onboarding. */
export const PATH_WEBINAR_PRESENTER = '/webinars/presenter';

export const QUICK_BOOKING = '/booking/book';

export const PATH_DOCS = {
  root: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
};

export const PATH_ZONE_ON_STORE =
  'https://mui.com/store/items/zone-landing-page/';

export const PATH_MINIMAL_ON_STORE =
  'https://mui.com/store/items/minimal-dashboard/';

export const PATH_FREE_VERSION =
  'https://mui.com/store/items/minimal-dashboard-free/';

export const PATH_FIGMA_PREVIEW =
  'https://www.figma.com/file/rWMDOkMZYw2VpTdNuBBCvN/%5BPreview%5D-Minimal-Web.26.11.22?node-id=0%3A1&t=ya2mDFiuhTXXLLF1-1';
